import { useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";

/**
 * useDebouncedCallback
 *
 * Returns a stable lodash-debounced wrapper around `fn` with the given `delay`.
 * Always calls the latest version of `fn` (stored in a ref), so callers don't
 * need to worry about stale closures. The debounce is cancelled automatically
 * on unmount. Exposes `.flush()` and `.cancel()` from lodash/debounce.
 *
 * @param {Function} fn    - The callback to debounce. Safe to pass an inline
 *                           function — the ref ensures the latest version is
 *                           always invoked without recreating the debounce timer.
 * @param {number}   delay - Debounce delay in milliseconds.
 * @returns {Function}       The debounced function (stable reference).
 *
 * @example
 * const debouncedSave = useDebouncedCallback((value) => saveToServer(value), 1000);
 *
 * // In a change handler:
 * debouncedSave(e.target.value);
 *
 * // In a blur handler (commit immediately without waiting for the timer):
 * debouncedSave.flush();
 */
const useDebouncedCallback = (fn, delay) => {
    const fnRef = useRef(fn);
    useEffect(() => { fnRef.current = fn; }, [fn]);

    const debounced = useMemo(
        () => debounce((...args) => fnRef.current?.(...args), delay),
        [delay]
    );

    // Cancel any pending invocation both when delay changes and when the
    // component unmounts. Since delay is typically a constant number literal,
    // the debounce instance is recreated at most once in practice.
    useEffect(() => () => debounced.cancel(), [debounced]);
    return debounced;
};

export default useDebouncedCallback;
